import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LineaCotizacion, TipoMarcacion, ColorVariante, EsquemaMarcacion, MarcacionOpcionFE, OrigenDescuento } from '../types';
import { ColorDropdown } from './ColorDropdown';
import { OpcionPrecioDropdown } from './OpcionPrecioDropdown';
import { MarcacionDropdown } from './MarcacionDropdown';
import { MarcacionModal } from './MarcacionModal';
import { calcularPrecioUnitario } from '../useDetalleCotizacion';
import { mostrarListaYDescuento } from '../domain/pricing';

interface ProductRowCardProps {
  gridTemplateColumns: string;
  linea: LineaCotizacion;
  cantidad: number;
  precioVenta: number;
  colorValue: string | null;
  marcacionValue: number | null;
  precioMarcacion: number;
  marcacionOpcionId: number | null;
  marcacionProveedorId: number | null;
  stockActual: number;
  precioProveedorActual: number;
  /** SPEC-195: % de descuento vigente en el detalle (tramo de la cantidad editada); por defecto, el de la línea. */
  descuentoProveedor?: number;
  /** SPEC-207 (RN-013): origen del % vigente en el detalle; por defecto, el de la línea. */
  origenDescuento?: OrigenDescuento | null;
  // SPEC-131 (RN-001): transporte vuelve a ser un dato por línea, editable.
  transporte: number;
  utilidad: number;
  tiposMarcacion: TipoMarcacion[];
  coloresVariantes: ColorVariante[];
  onCantidadChange: (id: string, v: number) => void;
  onTransporteChange: (id: string, v: number) => void;
  onPrecioVentaChange: (id: string, v: number) => void;
  onColorChange: (id: string, color: string, codigo: string | null) => void;
  // SPEC-208 (RN-007) — la tabla tiene la columna "Opción" (alguna línea con más de una opción); el
  // selector solo aparece en las líneas que la necesitan.
  mostrarOpcion?: boolean;
  onOpcionChange?: (id: string, indice: number) => void;
  onMarcacionChange: (id: string, tipoId: number | null) => void;
  onMarcacionConfirm: (
    id: string,
    tipoId: number,
    proveedorId: number,
    opcionId: number | null,
    precio: number,
    esquema: EsquemaMarcacion,
    opcionesFiltradas: MarcacionOpcionFE[],
  ) => void;
  onUtilidadChange: (id: string, v: number) => void;
  // SPEC-172 (RN-003): estas 3 acciones capturaban la línea por closure en el padre, lo que
  // recreaba la función por render y anulaba React.memo. Ahora la fila las invoca con su propio
  // linea/linea.id, y el padre pasa handlers referencialmente estables (useCallback).
  onProductoClick?: (linea: LineaCotizacion) => void;
  onDuplicar?: (id: string) => void;
  duplicando?: boolean;
  onBorrar?: (id: string) => void;
  soloLectura?: boolean;
}

function getStockLabel(stock: number): 'Alto' | 'Medio' | 'Bajo' {
  if (stock > 500) return 'Alto';
  if (stock > 100) return 'Medio';
  return 'Bajo';
}

function StockBadge({ valor }: { valor: number }) {
  const label = getStockLabel(valor);
  const cfg = {
    Alto: { bg: '#E4F8EB', color: '#20A75A' },
    Medio: { bg: '#FFF2E1', color: '#F39A1F' },
    Bajo: { bg: '#FDEBEC', color: '#F13C42' },
  }[label];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      <Box sx={{
        background: cfg.bg,
        color: cfg.color,
        fontSize: '14px',
        fontWeight: 600,
        px: '12px',
        py: '4px',
        borderRadius: '13px',
        height: '26px',
        display: 'flex',
        alignItems: 'center',
      }}>
        {label}
      </Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 400, color: '#28232D' }}>
        {valor.toLocaleString('es-CO')}
      </Typography>
    </Box>
  );
}

// SPEC-056: fontSize base 17px; SPEC-057: height 46px estándar
const inputSx = {
  '& .MuiOutlinedInput-root': {
    height: '56px',
    borderRadius: '6px',
    '& fieldset': { borderColor: '#DFDCE2' },
    '&:hover fieldset': { borderColor: '#9B2B9D' },
    '&.Mui-focused fieldset': { borderColor: '#9B2B9D', boxShadow: '0 0 0 3px rgba(155, 43, 157, 0.12)' },
  },
  '& .MuiInputBase-input': {
    fontSize: '17px',
    fontWeight: 400,
    color: '#28232D',
    padding: '0 14px',
  },
};

// SPEC-057: ocultar flechas de spinner en inputs type="number"
const noSpinnerSx = {
  '& input[type=number]': {
    MozAppearance: 'textfield',
    appearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
  '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
};

// SPEC-057: helpers para formato miles en tiempo real
function formatMiles(val: string): string {
  const digits = val.replace(/\D/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('es-CO');
}

function parseMiles(val: string): number {
  const digits = val.replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : 0;
}

// SPEC-172 (RN-003): fila memoizada. Con callbacks estables desde el padre, editar una línea
// solo re-renderiza esa ProductRowCard; las demás filas montadas conservan props idénticas.
export const ProductRowCard = React.memo(function ProductRowCard({
  gridTemplateColumns,
  linea,
  cantidad,
  precioVenta,
  colorValue,
  marcacionValue,
  precioMarcacion,
  marcacionOpcionId,
  marcacionProveedorId,
  stockActual,
  precioProveedorActual,
  descuentoProveedor,
  origenDescuento,
  transporte,
  utilidad,
  tiposMarcacion,
  coloresVariantes,
  onCantidadChange,
  onTransporteChange,
  onPrecioVentaChange,
  onColorChange,
  mostrarOpcion = false,
  onOpcionChange,
  onMarcacionChange,
  onMarcacionConfirm,
  onUtilidadChange,
  onProductoClick,
  onDuplicar,
  duplicando = false,
  onBorrar,
  soloLectura = false,
}: ProductRowCardProps) {
  const [cantidadInput, setCantidadInput] = useState(String(cantidad));
  const [modalMarcacionOpen, setModalMarcacionOpen] = useState(false);
  const [tipoMarcacionPendiente, setTipoMarcacionPendiente] = useState<number | null>(null);
  const [utilidadInput, setUtilidadInput] = useState(String(utilidad));
  const [transporteInput, setTransporteInput] = useState(transporte.toLocaleString('es-CO'));
  const [precioVentaInput, setPrecioVentaInput] = useState(
    precioVenta > 0 ? precioVenta.toLocaleString('es-CO') : '0'
  );

  useEffect(() => { setCantidadInput(String(cantidad)); }, [cantidad]);
  useEffect(() => { setUtilidadInput(String(utilidad)); }, [utilidad]);
  useEffect(() => { setTransporteInput(transporte.toLocaleString('es-CO')); }, [transporte]);
  useEffect(() => {
    setPrecioVentaInput(precioVenta > 0 ? precioVenta.toLocaleString('es-CO') : '0');
  }, [precioVenta]);

  // RN-002 (SPEC-131, restaura SPEC-095): "Precio Unitario" mostrado y usado como piso incluye
  // proveedor + marca + transporte de ESTA línea, prorrateado entre su propia cantidad.
  const precioProveedorConMarcacion = precioProveedorActual + precioMarcacion;
  const precioUnitario = calcularPrecioUnitario(precioProveedorConMarcacion, transporte, cantidad);
  const precioError = precioVenta < precioUnitario;
  const valorTransportePorUnidad = cantidad > 0 ? Math.round(transporte / cantidad) : 0;

  const handleMarcacionSelect = (tipoId: number | null) => {
    setTipoMarcacionPendiente(tipoId);
    setModalMarcacionOpen(true);
  };

  const handleModalConfirm = (
    tipoId: number,
    proveedorId: number,
    opcionId: number | null,
    precio: number,
    esquema: EsquemaMarcacion,
    opcionesFiltradas: MarcacionOpcionFE[],
  ) => {
    setModalMarcacionOpen(false);
    onMarcacionChange(linea.id, tipoId);
    onMarcacionConfirm(linea.id, tipoId, proveedorId, opcionId, precio, esquema, opcionesFiltradas);
    setTipoMarcacionPendiente(null);
  };

  const handleModalCancel = () => {
    setModalMarcacionOpen(false);
    setTipoMarcacionPendiente(null);
  };

  const tipoMarcacionActual = tipoMarcacionPendiente !== null
    ? tiposMarcacion.find(t => t.id === tipoMarcacionPendiente)
    : null;

  // SPEC-057: Cantidad — solo enteros
  const handleCantidadBlur = () => {
    const v = parseInt(cantidadInput, 10);
    if (!isNaN(v) && v >= 1) {
      onCantidadChange(linea.id, v);
    } else {
      setCantidadInput(String(cantidad));
    }
  };

  // SPEC-057: Utilidad — máx 1 decimal, redondeo en blur
  const handleUtilidadBlur = () => {
    const v = parseFloat(utilidadInput);
    if (!isNaN(v) && v >= 0) {
      const rounded = Math.round(v * 10) / 10;
      setUtilidadInput(String(rounded));
      onUtilidadChange(linea.id, rounded);
    } else {
      setUtilidadInput('0');
      onUtilidadChange(linea.id, 0);
    }
  };

  // SPEC-131 (VAL-001): Transporte — parsear miles, no permite negativos.
  const handleTransporteBlur = () => {
    const v = parseMiles(transporteInput);
    const final = v >= 0 ? v : 0;
    setTransporteInput(final.toLocaleString('es-CO'));
    onTransporteChange(linea.id, final);
  };

  // SPEC-057/SPEC-095: Precio venta — parsear miles, validar mínimo contra precio unitario completo
  const handlePrecioVentaBlur = () => {
    const v = parseMiles(precioVentaInput);
    const final = Math.max(v > 0 ? v : precioUnitario, precioUnitario);
    setPrecioVentaInput(final.toLocaleString('es-CO'));
    onPrecioVentaChange(linea.id, final);
  };

  return (
    <Box
      data-testid="product-row-card"
      sx={{
        display: 'grid',
        gridTemplateColumns,
        background: '#FFFFFF',
        borderBottom: '1px solid #E8E6EB',
        px: '14px',
        py: '16px',
        minHeight: '102px',
        alignItems: 'center',
        '&:hover': { backgroundColor: '#FCF7FC' },
      }}
    >
      {/* Producto — thumbnail 58×58 + nombre hasta 2 renglones */}
      <Box
        data-testid="product-cell-clickable"
        onClick={onProductoClick ? () => onProductoClick(linea) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          cursor: onProductoClick ? 'pointer' : 'default',
          borderRadius: '4px',
          ...(onProductoClick ? { '&:hover': { opacity: 0.82 } } : {}),
        }}
      >
        <Box
          sx={{
            width: '58px',
            height: '58px',
            borderRadius: '4px',
            background: '#F0EBF5',
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {linea.imagenUrl ? (
            <Box
              component="img"
              src={linea.imagenUrl}
              alt={linea.nombre}
              data-testid="product-thumbnail"
              sx={{ width: '58px', height: '58px', objectFit: 'cover' }}
            />
          ) : (
            <Box
              data-testid="product-img-placeholder"
              sx={{ width: 28, height: 28, borderRadius: '50%', background: '#D9C6D8' }}
            />
          )}
        </Box>
        <Typography sx={{
          fontWeight: 600,
          fontSize: '17px',
          color: '#32164B',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden',
        }}>
          {linea.nombre}
        </Typography>
      </Box>

      {/* Stock — más separación de Producto + centrado alineado con header */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <StockBadge valor={stockActual} />
      </Box>

      {/* Color — SPEC-057: centrado horizontalmente en su celda */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ColorDropdown
          colores={coloresVariantes}
          value={colorValue}
          onChange={(color, codigo) => onColorChange(linea.id, color, codigo)}
          disabled={soloLectura}
        />
      </Box>

      {/* Opción de precio — SPEC-208 (RN-010): celda solo si la tabla tiene la columna "Opción". */}
      {mostrarOpcion && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <OpcionPrecioDropdown
            opciones={linea.opcionesPrecio}
            value={linea.opcionPrecioIndice}
            onChange={(indice) => onOpcionChange?.(linea.id, indice)}
            disabled={soloLectura}
          />
        </Box>
      )}

      {/* Cantidad — SPEC-148 (RN-001): reubicada a la izquierda de Marcación. paddingLeft 16px separa de Color. */}
      <Box sx={{ paddingLeft: '16px' }}>
      <TextField
        type="number"
        value={cantidadInput}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9]/g, '');
          setCantidadInput(raw);
        }}
        onBlur={handleCantidadBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        disabled={soloLectura}
        inputProps={{ min: 1, inputMode: 'numeric', 'data-testid': 'input-cantidad' }}
        sx={{
          ...inputSx,
          ...noSpinnerSx,
          '& .MuiOutlinedInput-root': {
            height: '56px',
            borderRadius: '6px',
            '& fieldset': { borderColor: '#DFDCE2' },
            '&:hover fieldset': { borderColor: '#9B2B9D' },
            '&.Mui-focused fieldset': { borderColor: '#9B2B9D', boxShadow: '0 0 0 3px rgba(155, 43, 157, 0.12)' },
          },
          '& .MuiInputBase-input': {
            fontSize: '17px',
            fontWeight: 600,
            color: '#28232D',
            padding: '0 8px',
            textAlign: 'center',
          },
        }}
      />
      </Box>

      {/* Marcación — SPEC-148: Cantidad ya aporta la separación del grupo (izq); conserva 10px sep hacia Valor Marcación (der) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingRight: '10px' }}>
        <MarcacionDropdown
          tipos={tiposMarcacion}
          value={marcacionValue}
          onChange={(tipoId) => onMarcacionChange(linea.id, tipoId)}
          onMarcacionSelect={handleMarcacionSelect}
          disabled={soloLectura}
        />
      </Box>

      {tipoMarcacionActual && (
        <MarcacionModal
          open={modalMarcacionOpen}
          tipoMarcacion={tipoMarcacionActual}
          tiposMarcacion={tiposMarcacion}
          cantidad={cantidad}
          precioActual={precioMarcacion > 0 ? precioMarcacion : null}
          opcionActualId={marcacionOpcionId}
          proveedorMarcacionActualId={marcacionProveedorId}
          productoProveedorId={linea.proveedorId}
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
        />
      )}

      {/* Valor Marcación — SPEC-089: columna dedicada al precio unitario de marcación.
          SPEC-148 (RN-002): importe alineado a la derecha dentro de su celda. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Typography
          data-testid="valor-marcacion-unitario"
          sx={{ fontSize: '17px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#211D26' }}
        >
          $ {precioMarcacion.toLocaleString('es-CO')}
        </Typography>
      </Box>

      {/* Precio unitario — centrado (SPEC-056); tooltip desglosado (SPEC-085 + SPEC-094 + SPEC-095 RN-006) */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {(() => {
          // SPEC-195 (CA-017): el % vigente en el detalle, que sigue a la cantidad editada.
          const descuentoVigente = descuentoProveedor ?? linea.descuentoProveedor;
          // SPEC-207 (RN-014): "Lista · Descuento" con % > 0, o con origen costo_proveedor o neto.
          const origenVigente = origenDescuento !== undefined ? origenDescuento : (linea.origenDescuento ?? null);
          const tieneDescuento = mostrarListaYDescuento(descuentoVigente, origenVigente);
          const tieneMarca = precioMarcacion > 0;
          const tieneTransporte = transporte > 0;
          const tieneTooltip = tieneDescuento || tieneMarca || tieneTransporte;
          return (
            <Tooltip
              arrow
              placement="top"
              title={tieneTooltip
                ? (
                  <Box component="span" sx={{ display: 'block', lineHeight: 1.7 }}>
                    {tieneDescuento && <>Lista: ${linea.precioUnitario.toLocaleString('es-CO')}<br /></>}
                    {tieneDescuento && <>Descuento: {descuentoVigente}%<br /></>}
                    {tieneMarca && <>Valor marca: $ {precioMarcacion.toLocaleString('es-CO')}<br /></>}
                    {tieneTransporte && <>Valor transporte: $ {valorTransportePorUnidad.toLocaleString('es-CO')}</>}
                  </Box>
                )
                : ''
              }
            >
              <Box
                component="span"
                data-testid="precio-proveedor-valor"
                sx={{
                  fontSize: '17px',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#211D26',
                  cursor: tieneTooltip ? 'help' : 'default',
                }}
              >
                $ {precioUnitario.toLocaleString('es-CO')}
              </Box>
            </Tooltip>
          );
        })()}
      </Box>

      {/* Transporte — SPEC-131 (RN-001, CA-002): de vuelta como columna por línea, editable, mismo patrón que Utilidad %. */}
      <Box sx={{ paddingLeft: '10px' }}>
        <TextField
          type="text"
          value={transporteInput}
          onChange={(e) => setTransporteInput(formatMiles(e.target.value))}
          onBlur={handleTransporteBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          disabled={soloLectura}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'input-transporte' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontSize: '17px', color: '#8E8994', lineHeight: 1 }}>$</Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            ...inputSx,
            ...noSpinnerSx,
            '& .MuiInputBase-input': {
              fontSize: '17px',
              fontWeight: 400,
              color: '#28232D',
              padding: '0 8px 0 0',
              textAlign: 'right',
            },
          }}
        />
      </Box>

      {/* Utilidad % — SPEC-057: sin spinners, máx 1 decimal. SPEC-058: paddingLeft 10px sep de Transporte */}
      <Box sx={{ paddingLeft: '10px' }}>
      <TextField
        type="number"
        value={utilidadInput}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || /^\d*\.?\d{0,1}$/.test(val)) {
            setUtilidadInput(val);
          }
        }}
        onBlur={handleUtilidadBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        disabled={soloLectura}
        inputProps={{ min: 0, step: 0.1, inputMode: 'decimal', 'data-testid': 'input-utilidad' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: '17px', color: '#8E8994' }}>%</Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          ...inputSx,
          ...noSpinnerSx,
          '& .MuiInputBase-input': {
            fontSize: '17px',
            fontWeight: 400,
            color: '#28232D',
            padding: '0 4px 0 8px',
            textAlign: 'right',
          },
        }}
      />
      </Box>

      {/* Precio de venta — SPEC-063: 10px sep de Utilidad. SPEC-089: última columna de datos, borde izquierdo separador (antes en Total) */}
      <Box sx={{ paddingLeft: '10px', borderLeft: '2px solid #E8E4ED' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            type="text"
            value={precioVentaInput}
            onChange={(e) => setPrecioVentaInput(formatMiles(e.target.value))}
            onBlur={handlePrecioVentaBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            disabled={soloLectura}
            error={precioError}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'input-precio-venta' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontSize: '17px', color: '#8E8994', lineHeight: 1 }}>$</Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '56px',
                borderRadius: '6px',
                '& fieldset': { borderColor: precioError ? '#F13C42' : '#DFDCE2' },
                '&:hover fieldset': { borderColor: precioError ? '#F13C42' : '#9B2B9D' },
              },
              '& .MuiInputBase-input': {
                fontSize: '17px',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: '#211D26',
                padding: '0 8px 0 0',
                textAlign: 'right',
                width: '80px',
              },
            }}
          />
        </Box>
        {precioError && (
          <Typography data-testid="precio-venta-error" sx={{ fontSize: '12px', color: '#F13C42', mt: 0.3, ml: 2.5 }}>
            Mín: $ {precioUnitario.toLocaleString('es-CO')}
          </Typography>
        )}
      </Box>

      {/* Acciones — último, al extremo derecho de Precio de venta */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
        <IconButton
          size="small"
          disabled={duplicando || soloLectura}
          onClick={() => onDuplicar?.(linea.id)}
          data-testid="btn-duplicar"
          aria-label="duplicar"
          sx={{
            color: '#7C3E8E',
            width: '42px',
            height: '42px',
            border: '1px solid #E4E1E7',
            borderRadius: '6px',
            background: '#FFFFFF',
            '&:hover': { background: '#F4EAF4', borderColor: '#C8A0D8' },
            '&.Mui-disabled': { color: '#C8A0D8', background: '#FAFAFA' },
          }}
        >
          {duplicando
            ? <CircularProgress size={16} sx={{ color: '#7C3E8E' }} />
            : <ContentCopyIcon sx={{ fontSize: 18 }} />
          }
        </IconButton>
        <IconButton
          size="small"
          disabled={soloLectura}
          onClick={() => onBorrar?.(linea.id)}
          data-testid="btn-eliminar"
          sx={{
            color: '#E03030',
            width: '42px',
            height: '42px',
            border: '1px solid #E4E1E7',
            borderRadius: '6px',
            background: '#FFFFFF',
          }}
          aria-label="eliminar"
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Box>
  );
});
